import React, { useContext, useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import { UserContext } from "../services/UserContext";
import { API, authHeaders, resolveStorageUrl } from "../services/config";
import "./ConfiguracionPage.css";

const NAV_ITEMS_ALL = [
  { id: "membrete", label: "Membrete" },
  { id: "firma", label: "Pie de página" },
  { id: "whatsapp", label: "WhatsApp" },
];

const NAV_ITEMS_FREEMIUM = [{ id: "whatsapp", label: "WhatsApp" }];

export default function ConfiguracionPage() {
  const { user } = useContext(UserContext);
  const contentRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [qr, setQr] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [membreteModo, setMembreteModo] = useState("completo");
  const [membreteUrl, setMembreteUrl] = useState(null);
  const [membreteLogoUrl, setMembreteLogoUrl] = useState(null);
  const [membreteLogoFile, setMembreteLogoFile] = useState(null);
  const [membreteTexto, setMembreteTexto] = useState("");
  const [pieModo, setPieModo] = useState("completo");
  const [firmaUrl, setFirmaUrl] = useState(null);
  const [pieFirmaUrl, setPieFirmaUrl] = useState(null);
  const [pieFirmaFile, setPieFirmaFile] = useState(null);
  const [uploadingMembrete, setUploadingMembrete] = useState(false);
  const [savingEncabezado, setSavingEncabezado] = useState(false);
  const [uploadingFirma, setUploadingFirma] = useState(false);
  const [savingPie, setSavingPie] = useState(false);
  const [clinicaMsg, setClinicaMsg] = useState("");
  const [pieMsg, setPieMsg] = useState("");

  const isAdmin = user?.role === "admin";
  const navItems = isAdmin ? NAV_ITEMS_ALL : NAV_ITEMS_FREEMIUM;

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(`config-${sectionId}`);
    if (!el) return;
    const topbarOffset = 16;
    const top = el.getBoundingClientRect().top + window.scrollY - topbarOffset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const fetchConfigClinica = async () => {
    try {
      const res = await fetch(`${API}/configuracion-clinica`, { headers: authHeaders() });
      const json = await res.json();
      if (res.ok && json.success) {
        setMembreteModo(json.data?.membrete_modo || "completo");
        setMembreteUrl(resolveStorageUrl(json.data?.membrete_url));
        setMembreteLogoUrl(resolveStorageUrl(json.data?.membrete_logo_url));
        setMembreteTexto(json.data?.membrete_texto || "");
        setPieModo(json.data?.pie_modo || "completo");
        setFirmaUrl(resolveStorageUrl(json.data?.firma_sello_url));
        setPieFirmaUrl(resolveStorageUrl(json.data?.pie_firma_url));
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const fetchWaState = async () => {
      setLoading(true);
      setError("");
      try {
        const statusRes = await fetch(`${API}/whatsapp/status`, { headers: authHeaders() });
        const statusData = await statusRes.json();
        const isReady = Boolean(statusData?.ready);
        setReady(isReady);

        if (statusData?.error) {
          setError(statusData.error);
        } else {
          setError("");
        }

        if (!isReady) {
          const qrRes = await fetch(`${API}/whatsapp/qr`, { headers: authHeaders() });
          if (qrRes.ok) {
            const qrData = await qrRes.json();
            setQr(qrData?.qr || "");
          } else {
            setQr("");
          }
        } else {
          setQr("");
        }
      } catch {
        setError("No se pudo conectar con el servicio de WhatsApp.");
      } finally {
        setLoading(false);
      }
    };

    fetchWaState();
    if (isAdmin) {
      fetchConfigClinica();
    }
    const intervalId = window.setInterval(fetchWaState, 10000);
    return () => window.clearInterval(intervalId);
  }, [isAdmin]);

  const uploadClinica = async (endpoint, file, setUploading) => {
    const body = new FormData();
    body.append("archivo", file);
    setUploading(true);
    setClinicaMsg("");
    try {
      const res = await fetch(`${API}/configuracion-clinica/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: authHeaders().Authorization,
          Accept: "application/json",
        },
        body,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        if (endpoint === "membrete") {
          setMembreteModo(json.data?.membrete_modo || "completo");
          setMembreteUrl(resolveStorageUrl(json.data?.membrete_url));
          setClinicaMsg("Imagen guardada correctamente.");
        } else {
          setPieModo(json.data?.pie_modo || "completo");
          setFirmaUrl(resolveStorageUrl(json.data?.firma_sello_url));
          setPieMsg("Imagen guardada correctamente.");
        }
      } else {
        setClinicaMsg(json.message || "Error al subir la imagen.");
      }
    } catch {
      setClinicaMsg("Error de conexión al subir.");
    } finally {
      setUploading(false);
    }
  };

  const guardarConfiguracionEncabezado = async () => {
    const body = new FormData();
    body.append("modo", membreteModo);
    body.append("texto", membreteTexto);
    if (membreteLogoFile) body.append("logo", membreteLogoFile);

    setSavingEncabezado(true);
    setClinicaMsg("");
    try {
      const res = await fetch(`${API}/configuracion-clinica/encabezado`, {
        method: "POST",
        headers: {
          Authorization: authHeaders().Authorization,
          Accept: "application/json",
        },
        body,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setMembreteModo(json.data?.membrete_modo || membreteModo);
        setMembreteLogoUrl(resolveStorageUrl(json.data?.membrete_logo_url));
        setMembreteTexto(json.data?.membrete_texto || "");
        setMembreteLogoFile(null);
        setClinicaMsg("Encabezado guardado correctamente.");
      } else {
        setClinicaMsg(json.message || "Error al guardar el encabezado.");
      }
    } catch {
      setClinicaMsg("Error de conexión al guardar el encabezado.");
    } finally {
      setSavingEncabezado(false);
    }
  };

  const guardarConfiguracionPie = async () => {
    const body = new FormData();
    body.append("modo", pieModo);
    if (pieFirmaFile) body.append("imagen", pieFirmaFile);

    setSavingPie(true);
    setPieMsg("");
    try {
      const res = await fetch(`${API}/configuracion-clinica/pie`, {
        method: "POST",
        headers: {
          Authorization: authHeaders().Authorization,
          Accept: "application/json",
        },
        body,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setPieModo(json.data?.pie_modo || pieModo);
        setPieFirmaUrl(resolveStorageUrl(json.data?.pie_firma_url));
        setPieFirmaFile(null);
        setPieMsg("Pie de página guardado correctamente.");
      } else {
        setPieMsg(json.message || "Error al guardar el pie de página.");
      }
    } catch {
      setPieMsg("Error de conexión al guardar el pie de página.");
    } finally {
      setSavingPie(false);
    }
  };

  return (
    <div className="configuracion-page">
      <Sidebar />
      <main className="configuracion-content" ref={contentRef}>
        <h1>Configuración</h1>

        <nav className="config-nav" aria-label="Ir a sección">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="config-nav-btn"
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {isAdmin ? (
        <>
        <section id="config-membrete" className="config-section">
          <h2>Membrete (encabezado)</h2>
          <p className="configuracion-subtitle">
            Elegí cómo querés armar el encabezado de las prescripciones impresas.
          </p>

          {clinicaMsg ? <div className="config-alert info">{clinicaMsg}</div> : null}

          <div className="config-header-options">
            <button
              type="button"
              className={`config-option-card ${membreteModo === "completo" ? "active" : ""}`}
              onClick={() => setMembreteModo("completo")}
            >
              <strong>Subir membrete completo</strong>
              <span>Usá una imagen ya diseñada con logo, datos y estilo final.</span>
            </button>
            <button
              type="button"
              className={`config-option-card ${membreteModo === "armado" ? "active" : ""}`}
              onClick={() => setMembreteModo("armado")}
            >
              <strong>Armar con logo + texto</strong>
              <span>Subí el logo y escribí dirección, teléfono, horarios u otros datos.</span>
            </button>
          </div>

          {membreteModo === "completo" ? (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 1: membrete completo</h3>
              <p className="config-muted">
                Recomendado si ya tenés una imagen final lista para ocupar todo el encabezado del PDF.
              </p>
              {membreteUrl ? (
                <img src={membreteUrl} alt="Membrete actual" className="config-preview-img" />
              ) : (
                <p className="config-muted">Sin membrete cargado.</p>
              )}
              <div className="config-actions-row">
              <label className="config-upload-label">
                <input
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="config-file-input"
                  disabled={uploadingMembrete}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadClinica("membrete", f, setUploadingMembrete);
                    e.target.value = "";
                  }}
                />
                {uploadingMembrete ? "Subiendo..." : "Subir membrete completo"}
              </label>
                <button
                  type="button"
                  className="config-save-btn config-save-secondary"
                  onClick={guardarConfiguracionEncabezado}
                  disabled={savingEncabezado}
                >
                  {savingEncabezado ? "Guardando..." : "Usar este modo"}
                </button>
              </div>
            </div>
          ) : (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 2: armar encabezado</h3>
              <p className="config-muted">
                El PDF mostrará el logo centrado, debajo el texto centrado y una línea horizontal para cerrar el encabezado.
              </p>

              <div className="config-built-preview" aria-label="Vista previa del encabezado armado">
                {membreteLogoFile || membreteLogoUrl ? (
                  <img
                    src={membreteLogoFile ? URL.createObjectURL(membreteLogoFile) : membreteLogoUrl}
                    alt="Logo del encabezado"
                    className="config-logo-preview"
                  />
                ) : (
                  <div className="config-logo-placeholder">Logo</div>
                )}
                <p>{membreteTexto || "Dirección, teléfono, horarios, matrícula u otros datos del consultorio"}</p>
                <span />
              </div>

              <label className="config-field-label">Logo del encabezado</label>
              <label className="config-upload-label config-upload-secondary">
                <input
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="config-file-input"
                  disabled={savingEncabezado}
                  onChange={(e) => {
                    setMembreteLogoFile(e.target.files?.[0] || null);
                  }}
                />
                {membreteLogoFile ? membreteLogoFile.name : "Seleccionar logo"}
              </label>

              <label className="config-field-label" htmlFor="config-membrete-texto">
                Texto centrado del encabezado
              </label>
              <textarea
                id="config-membrete-texto"
                className="config-textarea"
                rows={4}
                value={membreteTexto}
                onChange={(e) => setMembreteTexto(e.target.value)}
                placeholder={"Ej: Av. Siempre Viva 123 - Córdoba\nTel: 351 000-0000\nAtención de lunes a viernes"}
              />

              <button
                type="button"
                className="config-save-btn"
                onClick={guardarConfiguracionEncabezado}
                disabled={savingEncabezado}
              >
                {savingEncabezado ? "Guardando..." : "Guardar encabezado armado"}
              </button>
            </div>
          )}
        </section>

        <section id="config-firma" className="config-section">
          <h2>Pie de página (firma y sello)</h2>
          <p className="configuracion-subtitle">
            Opcional. Elegí cómo querés mostrar la firma al imprimir prescripciones.
          </p>

          {pieMsg ? <div className="config-alert info">{pieMsg}</div> : null}

          <div className="config-header-options">
            <button
              type="button"
              className={`config-option-card ${pieModo === "completo" ? "active" : ""}`}
              onClick={() => setPieModo("completo")}
            >
              <strong>Subir imagen completa</strong>
              <span>Usá una imagen ya armada con firma, sello y datos del pie.</span>
            </button>
            <button
              type="button"
              className={`config-option-card ${pieModo === "armado" ? "active" : ""}`}
              onClick={() => setPieModo("armado")}
            >
              <strong>Armar con línea + firma</strong>
              <span>El PDF mostrará una línea horizontal y debajo la imagen de firma o sello.</span>
            </button>
          </div>

          {pieModo === "completo" ? (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 1: pie completo</h3>
              <p className="config-muted">
                Recomendado si ya tenés una imagen final del pie de página lista para el PDF.
              </p>
              {firmaUrl ? (
                <img src={firmaUrl} alt="Pie de página actual" className="config-preview-img config-preview-firma" />
              ) : (
                <p className="config-muted">Sin pie de página cargado.</p>
              )}
              <div className="config-actions-row">
                <label className="config-upload-label">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                    className="config-file-input"
                    disabled={uploadingFirma}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadClinica("firma", f, setUploadingFirma);
                      e.target.value = "";
                    }}
                  />
                  {uploadingFirma ? "Subiendo..." : "Subir pie completo"}
                </label>
                <button
                  type="button"
                  className="config-save-btn config-save-secondary"
                  onClick={guardarConfiguracionPie}
                  disabled={savingPie}
                >
                  {savingPie ? "Guardando..." : "Usar este modo"}
                </button>
              </div>
            </div>
          ) : (
            <div className="config-card config-card-wide">
              <h3 className="config-card-title">Opción 2: línea + firma o sello</h3>
              <p className="config-muted">
                El PDF dibujará una línea horizontal y, debajo, la imagen que subas (firma, sello o ambos).
              </p>

              <div className="config-built-preview config-built-preview-pie" aria-label="Vista previa del pie de página">
                <span />
                {pieFirmaFile || pieFirmaUrl ? (
                  <img
                    src={pieFirmaFile ? URL.createObjectURL(pieFirmaFile) : pieFirmaUrl}
                    alt="Firma o sello"
                    className="config-logo-preview config-preview-firma"
                  />
                ) : (
                  <div className="config-logo-placeholder">Firma / sello</div>
                )}
              </div>

              <label className="config-field-label">Imagen de firma o sello</label>
              <label className="config-upload-label config-upload-secondary">
                <input
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="config-file-input"
                  disabled={savingPie}
                  onChange={(e) => {
                    setPieFirmaFile(e.target.files?.[0] || null);
                  }}
                />
                {pieFirmaFile ? pieFirmaFile.name : "Seleccionar imagen"}
              </label>

              <button
                type="button"
                className="config-save-btn"
                onClick={guardarConfiguracionPie}
                disabled={savingPie}
              >
                {savingPie ? "Guardando..." : "Guardar pie de página armado"}
              </button>
            </div>
          )}
        </section>
        </>
        ) : null}

        <section id="config-whatsapp" className="config-section config-section-last">
          <h2>WhatsApp</h2>
          <p className="configuracion-subtitle">
            Estado de conexión del bot para confirmaciones y recordatorios de turnos.
          </p>

          <div className="config-wa-instructions" role="note">
            <h3>Cómo vincular tu WhatsApp</h3>
            <ol>
              <li>
                <strong>Abrí esta pantalla desde una computadora</strong> (no desde el celular) para ver el código QR.
              </li>
              <li>
                En tu teléfono, abrí WhatsApp → <em>Dispositivos vinculados</em> → <em>Vincular dispositivo</em>.
              </li>
              <li>
                <strong>Escaneá el código QR una sola vez.</strong> La vinculación queda guardada.
              </li>
              <li>
                Después podés usar Turnero normalmente desde el celular; no hace falta volver a escanear.
              </li>
            </ol>
          </div>

          {error ? <div className="config-alert error">{error}</div> : null}

          <div className="config-card">
            <span className={`status-badge ${ready ? "connected" : "disconnected"}`}>
              {ready ? "Conectado" : "Sin conectar"}
            </span>

            {loading ? <p>Cargando estado...</p> : null}

            {!ready && !loading && qr ? (
              <div className="qr-wrapper">
                <p>Escaneá este QR con la app de WhatsApp del número del bot.</p>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(qr)}`}
                  alt="QR de WhatsApp"
                />
              </div>
            ) : null}

            {!ready && !loading && !qr ? (
              <p>No hay QR disponible todavía. Revisá que el microservicio esté iniciado.</p>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}
